import { useEffect, useMemo, useReducer, useRef } from "react";
import type { ControlClient } from "../api/client.ts";
import type { Device, DevicePage } from "../api/parse.ts";
import type { DeviceFolder, DeviceRow, PlacementCommit } from "./devices.tsx";
import { visibleFailure } from "../stepUp.ts";
import { formatHeartbeatLocal } from "./heartbeat.ts";

export const PAGE_LIMIT = 200;
export const PAGE_CAP = 25;
export const WALK_BUDGET_MS = 30_000;
export const REQUEST_TIMEOUT_MS = 10_000;
export const SINGLE_PAGE_POLL_MS = 15_000;
export const MULTI_PAGE_POLL_FLOOR_MS = 60_000;

export type InventoryLoad = "loading" | "partial" | "complete";

export type WalkClock = {
  mono: () => number;
  wait: (ms: number) => Promise<void>;
};

const systemClock: WalkClock = {
  mono: () => performance.now(),
  wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
};

function resolveClock(clock: Partial<WalkClock>): WalkClock {
  return { mono: clock.mono ?? systemClock.mono, wait: clock.wait ?? systemClock.wait };
}

type Live = {
  presence: Device["presence"];
  lastHeartbeatAt: string | null;
  agentVersion: string;
  name: string;
  hostname: string;
  osLabel: string;
  lastBootAt: string | null;
  loggedOnUser: string | null;
};

type Catalog = {
  deviceId: string;
  status: Device["status"];
  organisationId: string | null;
  organisationName: string | null;
  departmentId: string | null;
  departmentName: string | null;
  tags: Device["tags"];
  recordingSelectable: boolean;
  recordingForced: boolean;
  stealthCommand: boolean;
  reasonRequired?: boolean;
  platform: string;
  osVersion: string;
  osArchitecture: string;
  capabilities?: string[];
  minimumAgentVersion?: string | null;
  protocolVersion?: string | null;
  environment?: string | null;
  createdAt: string | null;
  revokedAt: string | null;
  revokeReason: string | null;
  displaySummary: string;
};

type Member = {
  catalog: Catalog;
  live: Live;
};

type Inventory = {
  members: Member[];
  folders: DeviceFolder[];
  load: InventoryLoad;
  error: string | null;
};

const empty: Inventory = { members: [], folders: [], load: "loading", error: null };

type InventoryEvent =
  | { type: "sign-out" }
  | { type: "loaded"; members: Member[]; folders: DeviceFolder[]; partial: boolean }
  | { type: "load-failed"; error: string }
  | { type: "publish"; stamps: ReadonlyMap<string, Live> }
  | { type: "note-folder"; organisationId: string; organisationName: string; departmentId: string; departmentName: string }
  | { type: "apply-device"; device: Device }
  | { type: "remove-device"; deviceId: string };

function sameLive(left: Live, right: Live): boolean {
  return (
    left.presence === right.presence &&
    left.lastHeartbeatAt === right.lastHeartbeatAt &&
    left.agentVersion === right.agentVersion &&
    left.name === right.name &&
    left.hostname === right.hostname &&
    left.osLabel === right.osLabel &&
    left.lastBootAt === right.lastBootAt &&
    left.loggedOnUser === right.loggedOnUser
  );
}

function reduce(state: Inventory, event: InventoryEvent): Inventory {
  switch (event.type) {
    case "sign-out":
      return state.members.length === 0 && state.folders.length === 0 && state.load === "loading" && state.error === null
        ? state
        : empty;
    case "loaded":
      return { members: event.members, folders: event.folders, load: event.partial ? "partial" : "complete", error: null };
    case "load-failed":
      return { ...state, error: event.error };
    case "publish": {
      let changed = false;
      const members = state.members.map((member) => {
        const stamp = event.stamps.get(member.catalog.deviceId);
        if (!stamp || sameLive(member.live, stamp)) return member;
        changed = true;
        return { catalog: member.catalog, live: stamp };
      });
      if (!changed) return state;
      return { ...state, members };
    }
    case "note-folder": {
      const folders = state.folders.map((folder) => ({ ...folder, departments: folder.departments.slice() }));
      const orgIndex = folders.findIndex((folder) => folder.organisationId === event.organisationId);
      const department = { departmentId: event.departmentId, name: event.departmentName };
      if (orgIndex < 0) {
        folders.push({
          organisationId: event.organisationId,
          name: event.organisationName,
          departments: [department],
        });
      } else if (!folders[orgIndex].departments.some((item) => item.departmentId === event.departmentId)) {
        folders[orgIndex].departments.push(department);
      }
      return { ...state, folders };
    }
    case "apply-device": {
      let changed = false;
      const members = state.members.map((member) => {
        if (member.catalog.deviceId !== event.device.deviceId) return member;
        changed = true;
        const organisationId = event.device.organisationId ?? null;
        const departmentId = event.device.departmentId ?? null;
        const folder = organisationId ? state.folders.find((item) => item.organisationId === organisationId) : undefined;
        const department = departmentId ? folder?.departments.find((item) => item.departmentId === departmentId) : undefined;
        return {
          catalog: {
            ...member.catalog,
            status: event.device.status,
            tags: event.device.tags,
            recordingSelectable: event.device.recordingSelectable === true,
            recordingForced: event.device.recordingForced === true,
            stealthCommand: event.device.stealthCommand === true,
            reasonRequired: event.device.reasonRequired === true,
            platform: event.device.os.platform,
            osVersion: event.device.os.version,
            osArchitecture: event.device.os.architecture,
            capabilities: event.device.capabilities,
            minimumAgentVersion: event.device.minimumAgentVersion ?? null,
            protocolVersion: event.device.protocolVersion ?? null,
            environment: event.device.environment ?? null,
            createdAt: event.device.createdAt ?? null,
            revokedAt: event.device.revokedAt ?? null,
            revokeReason: event.device.revokeReason ?? null,
            displaySummary: summarizeDisplays(event.device.displays),
            organisationId,
            organisationName: folder?.name ?? (organisationId ? member.catalog.organisationName : null),
            departmentId,
            departmentName: department?.name ?? (departmentId ? member.catalog.departmentName : null),
          },
          live: liveFrom(event.device),
        };
      });
      return changed ? { ...state, members, error: null } : state;
    }
    case "remove-device":
      return {
        ...state,
        members: state.members.filter((member) => member.catalog.deviceId !== event.deviceId),
        error: null,
      };
  }
}

function enrolledLabel(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || Number.isNaN(Date.parse(iso))) return "—";
  return formatHeartbeatLocal(iso);
}

function revokedLabel(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || Number.isNaN(Date.parse(iso))) return "—";
  return formatHeartbeatLocal(iso);
}

function summarizeDisplays(displays: Device["displays"]): string {
  if (displays.length === 0) return "—";
  const primary = displays.find((item) => item.primary) ?? displays[0];
  return displays.length === 1 ? primary.name : `${displays.length} displays (${primary.name})`;
}

function project(members: readonly Member[]): DeviceRow[] {
  return members.map((member) => ({
    deviceId: member.catalog.deviceId,
    name: member.live.name,
    hostname: member.live.hostname,
    presence: member.live.presence,
    lastHeartbeatAt: member.live.lastHeartbeatAt,
    lastHeartbeatLabel: formatHeartbeatLocal(member.live.lastHeartbeatAt),
    lastBootAt: member.live.lastBootAt,
    lastBootLabel: bootLabel(member.live.lastBootAt),
    loggedOnUser: member.live.loggedOnUser && member.live.loggedOnUser.length > 0 ? member.live.loggedOnUser : "None",
    status: member.catalog.status,
    agentVersion: member.live.agentVersion,
    osLabel: member.live.osLabel,
    organisationId: member.catalog.organisationId,
    organisationName: member.catalog.organisationName,
    departmentId: member.catalog.departmentId,
    departmentName: member.catalog.departmentName,
    tags: member.catalog.tags,
    recordingSelectable: member.catalog.recordingSelectable === true,
    recordingForced: member.catalog.recordingForced === true,
    stealthCommand: member.catalog.stealthCommand,
    reasonRequired: member.catalog.reasonRequired === true,
    platform: member.catalog.platform,
    osVersion: member.catalog.osVersion,
    osArchitecture: member.catalog.osArchitecture,
    capabilities: member.catalog.capabilities,
    minimumAgentVersion: member.catalog.minimumAgentVersion,
    protocolVersion: member.catalog.protocolVersion,
    environment: member.catalog.environment,
    createdAt: member.catalog.createdAt,
    createdAtLabel: enrolledLabel(member.catalog.createdAt),
    approvedAtLabel: "—",
    revokedAt: member.catalog.revokedAt,
    revokedAtLabel: revokedLabel(member.catalog.revokedAt),
    revokeReason: member.catalog.revokeReason,
    lastSeenIp: null,
    displaySummary: member.catalog.displaySummary,
  }));
}

function bootLabel(lastBootAt: string | null): string {
  if (lastBootAt === null || Number.isNaN(Date.parse(lastBootAt))) return "Unknown";
  return formatHeartbeatLocal(lastBootAt);
}

function osLabel(device: Device): string {
  const version = device.os.version.trim();
  return version.length > 0 ? version : `${device.os.platform} ${device.os.architecture}`;
}

function liveFrom(device: Device): Live {
  return {
    presence: device.presence,
    lastHeartbeatAt: device.lastHeartbeatAt ?? null,
    agentVersion: device.agentVersion,
    name: device.name,
    hostname: device.hostname,
    osLabel: osLabel(device),
    lastBootAt: device.lastBootAt ?? null,
    loggedOnUser: device.loggedOnUser ?? null,
  };
}

type Catalogue = {
  folders: DeviceFolder[];
  orgNames: Map<string, string>;
  departments: Map<string, string>;
};

function catalogFrom(device: Device, catalogue: Catalogue): Catalog {
  return {
    deviceId: device.deviceId,
    status: device.status,
    organisationId: device.organisationId ?? null,
    organisationName: device.organisationId ? (catalogue.orgNames.get(device.organisationId) ?? null) : null,
    departmentId: device.departmentId ?? null,
    departmentName: device.departmentId ? (catalogue.departments.get(device.departmentId) ?? null) : null,
    tags: device.tags,
    recordingSelectable: device.recordingSelectable === true,
    recordingForced: device.recordingForced === true,
    stealthCommand: device.stealthCommand === true,
    reasonRequired: device.reasonRequired === true,
    platform: device.os.platform,
    osVersion: device.os.version,
    osArchitecture: device.os.architecture,
    capabilities: device.capabilities,
    minimumAgentVersion: device.minimumAgentVersion ?? null,
    protocolVersion: device.protocolVersion ?? null,
    environment: device.environment ?? null,
    createdAt: device.createdAt ?? null,
    revokedAt: device.revokedAt ?? null,
    revokeReason: device.revokeReason ?? null,
    displaySummary: summarizeDisplays(device.displays),
  };
}

async function loadCatalogue(client: ControlClient): Promise<Catalogue> {
  const organisations = await client.listOrganisations();
  const departments = new Map<string, string>();
  const folders: DeviceFolder[] = [];
  for (const org of organisations) {
    const rows = await client.listDepartments(org.organisationId);
    folders.push({
      organisationId: org.organisationId,
      name: org.name,
      departments: rows.map((row) => ({ departmentId: row.departmentId, name: row.name })),
    });
    for (const row of rows) departments.set(row.departmentId, row.name);
  }
  return { folders, orgNames: new Map(organisations.map((org) => [org.organisationId, org.name])), departments };
}

type WalkPace = { pages: number; durationMs: number };

type PageWalk = WalkPace & { devices: Device[]; partial: boolean };

export type InventoryWalk = WalkPace & { members: Member[]; folders: DeviceFolder[]; partial: boolean };

const timedOut = Symbol("timed out");

function fetchPage(client: ControlClient, cursor: string | undefined, clock: WalkClock): Promise<DevicePage | typeof timedOut> {
  return Promise.race([
    client.listDevices(cursor, undefined, PAGE_LIMIT),
    clock.wait(REQUEST_TIMEOUT_MS).then((): typeof timedOut => timedOut),
  ]);
}

async function walkPages(client: ControlClient, clock: WalkClock, start: number): Promise<PageWalk> {
  const devices = new Map<string, Device>();
  const followed = new Set<string>();
  let cursor: string | undefined;
  let pages = 0;
  const finish = (partial: boolean): PageWalk => ({
    devices: [...devices.values()],
    partial,
    pages,
    durationMs: clock.mono() - start,
  });
  for (;;) {
    if (pages > 0 && clock.mono() - start >= WALK_BUDGET_MS) return finish(true);
    let page: DevicePage | typeof timedOut;
    try {
      page = await fetchPage(client, cursor, clock);
    } catch (cause) {
      if (pages === 0) throw cause;
      return finish(true);
    }
    if (page === timedOut) {
      if (pages === 0) throw new Error("Could not load devices");
      return finish(true);
    }
    pages += 1;
    for (const device of page.items) devices.set(device.deviceId, device);
    const next = page.nextCursor ? page.nextCursor : null;
    if (next === null) return finish(false);
    if (pages >= PAGE_CAP || followed.has(next)) return finish(true);
    followed.add(next);
    cursor = next;
  }
}

export async function walkInventory(client: ControlClient, clock: Partial<WalkClock> = {}): Promise<InventoryWalk> {
  const resolved = resolveClock(clock);
  const start = resolved.mono();
  const catalogue = await loadCatalogue(client);
  const walk = await walkPages(client, resolved, start);
  return {
    members: walk.devices.map((device) => ({ catalog: catalogFrom(device, catalogue), live: liveFrom(device) })),
    folders: catalogue.folders,
    partial: walk.partial,
    pages: walk.pages,
    durationMs: walk.durationMs,
  };
}

function nextPollMs(pace: WalkPace | null): number {
  if (pace === null || pace.pages <= 1) return SINGLE_PAGE_POLL_MS;
  return Math.max(MULTI_PAGE_POLL_FLOOR_MS, 2 * pace.durationMs);
}

function failureText(cause: unknown): string | null {
  return visibleFailure(cause, "Could not load devices");
}

export function useDeviceInventory(
  client: ControlClient,
  signedIn: boolean,
  clock: Partial<WalkClock> = {},
): {
  rows: DeviceRow[];
  catalog: DeviceFolder[];
  error: string | null;
  load: InventoryLoad;
  approve: (deviceId: string) => void;
  rename: (deviceId: string, name: string) => void;
  place: (deviceId: string, organisationId: string, departmentId: string) => void;
  commit: (deviceId: string, input: PlacementCommit) => void;
  remove: (deviceId: string) => void;
} {
  const [state, dispatch] = useReducer(reduce, empty);
  const stateRef = useRef(state);
  stateRef.current = state;
  const clientRef = useRef(client);
  clientRef.current = client;
  const clockRef = useRef(clock);
  clockRef.current = clock;
  const signedInRef = useRef(signedIn);
  signedInRef.current = signedIn;
  const epoch = useRef(0);
  const walking = useRef(false);
  const pace = useRef<WalkPace | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const signedInSeen = useRef(signedIn);

  if (signedInSeen.current !== signedIn) {
    signedInSeen.current = signedIn;
    if (!signedIn) dispatch({ type: "sign-out" });
  }

  function accepting(): boolean {
    return signedInRef.current && document.visibilityState === "visible";
  }

  function clearTimer(): void {
    if (timer.current === null) return;
    clearTimeout(timer.current);
    timer.current = null;
  }

  function arm(): void {
    clearTimer();
    if (!accepting()) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      void walk();
    }, nextPollMs(pace.current));
  }

  async function walk(): Promise<void> {
    if (walking.current || !accepting()) return;
    clearTimer();
    walking.current = true;
    const ticket = epoch.current;
    const current = stateRef.current;
    const patching = current.load === "complete" && current.members.length > 0;
    const walkClock = resolveClock(clockRef.current);
    try {
      if (patching) {
        const walked = await walkPages(clientRef.current, walkClock, walkClock.mono());
        if (ticket !== epoch.current) return;
        pace.current = walked;
        if (!accepting()) return;
        dispatch({ type: "publish", stamps: new Map(walked.devices.map((device) => [device.deviceId, liveFrom(device)])) });
        return;
      }
      const loaded = await walkInventory(clientRef.current, walkClock);
      if (ticket !== epoch.current) return;
      pace.current = loaded;
      if (!accepting()) return;
      dispatch({ type: "loaded", members: loaded.members, folders: loaded.folders, partial: loaded.partial });
    } catch (cause) {
      if (ticket !== epoch.current || !accepting() || patching) return;
      const error = failureText(cause);
      if (error) dispatch({ type: "load-failed", error });
    } finally {
      if (ticket === epoch.current) {
        walking.current = false;
        arm();
      }
    }
  }

  useEffect(() => {
    if (!signedIn) {
      epoch.current += 1;
      walking.current = false;
      clearTimer();
      return;
    }
    function onVisibility(): void {
      if (!accepting()) {
        clearTimer();
        return;
      }
      void walk();
    }
    document.addEventListener("visibilitychange", onVisibility);
    void walk();
    return () => {
      epoch.current += 1;
      walking.current = false;
      clearTimer();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [signedIn]);

  function approve(deviceId: string): void {
    void apply(clientRef.current.approveDevice(deviceId));
  }

  function rename(deviceId: string, name: string): void {
    void apply(clientRef.current.renameDevice(deviceId, name));
  }

  function place(deviceId: string, organisationId: string, departmentId: string): void {
    void apply(clientRef.current.placeDevice(deviceId, organisationId, departmentId));
  }

  function commit(deviceId: string, input: PlacementCommit): void {
    void savePlacement(deviceId, input);
  }

  function remove(deviceId: string): void {
    const client = clientRef.current;
    if (!client.deleteDevice) return;
    void (async () => {
      try {
        await client.deleteDevice!(deviceId);
        if (!signedInRef.current) return;
        dispatch({ type: "remove-device", deviceId });
      } catch (cause) {
        if (!signedInRef.current) return;
        const error = failureText(cause);
        if (error) dispatch({ type: "load-failed", error });
      }
    })();
  }

  async function savePlacement(deviceId: string, input: PlacementCommit): Promise<void> {
    const client = clientRef.current;
    const row = stateRef.current.members.find((member) => member.catalog.deviceId === deviceId);
    if (!row) return;
    try {
      let device = await reloadDevice(client, row, input.name);
      const orgName = input.organisationName.trim();
      const deptName = input.departmentName.trim();
      if (orgName.length > 0 && deptName.length > 0) {
        const folders = stateRef.current.folders;
        const orgMatch = folders.find((folder) => folder.name.trim().toLowerCase() === orgName.toLowerCase());
        const org = orgMatch ?? (await client.createOrganisation(orgName));
        const deptMatch = orgMatch?.departments.find((department) => department.name.trim().toLowerCase() === deptName.toLowerCase());
        const dept = deptMatch
          ? { departmentId: deptMatch.departmentId, organisationId: org.organisationId, name: deptMatch.name }
          : await client.createDepartment(org.organisationId, deptName);
        const changed =
          row.catalog.organisationId !== org.organisationId || row.catalog.departmentId !== dept.departmentId;
        if (changed) device = await client.placeDevice(deviceId, org.organisationId, dept.departmentId);
        dispatch({
          type: "note-folder",
          organisationId: org.organisationId,
          organisationName: org.name,
          departmentId: dept.departmentId,
          departmentName: dept.name,
        });
      }
      if (!sameTags(row.catalog.tags, input.tags)) {
        device = await client.replaceDeviceTags(deviceId, input.tags.map((tag) => ({ key: tag.key.trim(), value: tag.value.trim() })));
      }
      if (!signedInRef.current) return;
      dispatch({ type: "apply-device", device });
    } catch (cause) {
      if (!signedInRef.current) return;
      const error = failureText(cause);
      if (error) dispatch({ type: "load-failed", error });
    }
  }

  async function apply(pending: Promise<Device>): Promise<void> {
    try {
      const device = await pending;
      if (!signedInRef.current) return;
      dispatch({ type: "apply-device", device });
    } catch (cause) {
      if (!signedInRef.current) return;
      const error = failureText(cause);
      if (error) dispatch({ type: "load-failed", error });
    }
  }

  const rows = useMemo(() => project(state.members), [state.members]);

  return {
    rows,
    catalog: state.folders,
    error: state.error,
    load: state.load,
    approve,
    rename,
    place,
    commit,
    remove,
  };
}

function sameTags(left: { key: string; value: string }[], right: { key: string; value: string }[]): boolean {
  const label = (tag: { key: string; value: string }) => `${tag.key.trim().toLowerCase()}:${tag.value.trim().toLowerCase()}`;
  const a = left.map(label).sort();
  const b = right.map(label).sort();
  return a.length === b.length && a.every((item, index) => item === b[index]);
}

async function reloadDevice(
  client: ControlClient,
  row: { catalog: { deviceId: string }; live: { name: string } },
  name: string,
): Promise<Device> {
  if (name.length > 0 && name !== row.live.name) return client.renameDevice(row.catalog.deviceId, name);
  const page = await client.listDevices();
  const found = page.items.find((item) => item.deviceId === row.catalog.deviceId);
  if (!found) throw new Error("Device was not found.");
  return found;
}
